import { Reducer } from 'react';
import { GroupApiId } from '@/model/TreatmentGroup.model';
import { AnimalApiId } from '@/model/Animal.model';
import { unionSets } from '@/utils/browser.utils';
import { components } from '@/generated/internal-api/openapi-types';
import { AnimalDefinitionApiId } from '@/model/PlannedAnimal.model';
import {
  GroupTarget,
  PlannedAnimalTarget,
  RegisteredAnimalTarget,
  SampleDefinitionFormData,
  SampleDefinitionTargetFormData,
  TargetApiId,
} from '@/model/SamplePlan.model';
import { _notNil } from '@/littledash';
import { PlannedSampleOption } from '@/components/Samples/AssignAnimals/AssignAnimalsProvider';
import { Nullable } from '@/model/Common.model';

export type SampleDefinitionTargetV1 = components['schemas']['SampleDefinitionTargetV1.schema'];
export type TargetType = components['schemas']['SampleDefinitionTargetV1.schema']['type'];
export type AssignAnimalState = {
  activeTargetType: Exclude<TargetType, 'no_target'>;
  selectedGroupTargets: Set<GroupTarget['api_id']>;
  selectedRegisteredAnimalTargets: Set<RegisteredAnimalTarget['api_id']>;
  selectedPlannedAnimalTargets: Set<PlannedAnimalTarget['api_id']>;
  targetToPlannedSamples: Map<TargetApiId, Set<SampleDefinitionFormData['api_id']>>;
  plannedSampleToTargets: Map<SampleDefinitionFormData['api_id'], Set<TargetApiId>>;
  plannedSampleToTargetType: Map<SampleDefinitionFormData['api_id'], TargetType>;
};
export type AssignAnimalAction =
  | { type: 'update-active-target-type'; value: Exclude<TargetType, 'no_target'> }
  | { type: 'update-group-target-selection'; value: Set<GroupTarget['api_id']> }
  | { type: 'update-planned-animal-target-selection'; value: Set<PlannedAnimalTarget['api_id']> }
  | { type: 'update-registered-animal-target-selection'; value: Set<RegisteredAnimalTarget['api_id']> }
  | { type: 'update-planned-sample-selection'; value: Map<SampleDefinitionFormData['api_id'], boolean> };

const getCurrentlySelectedTargets = (state: AssignAnimalState): Set<TargetApiId> => {
  switch (state.activeTargetType) {
    case 'groups':
      return state.selectedGroupTargets;
    case 'planned_animals':
      return state.selectedPlannedAnimalTargets;
    case 'registered_animals':
      return state.selectedRegisteredAnimalTargets;
  }
};

const handleSampleSelection = (
  plannedSampleId: SampleDefinitionFormData['api_id'],
  currentTargets: Set<TargetApiId>,
  activeTargetType: TargetType,
  maps: {
    plannedSampleToTargetType: Map<SampleDefinitionFormData['api_id'], TargetType>;
    plannedSampleToTargets: Map<SampleDefinitionFormData['api_id'], Set<TargetApiId>>;
    targetToPlannedSamples: Map<TargetApiId, Set<SampleDefinitionFormData['api_id']>>;
  }
) => {
  const { plannedSampleToTargetType, plannedSampleToTargets, targetToPlannedSamples } = maps;

  if (!plannedSampleToTargetType.has(plannedSampleId)) {
    plannedSampleToTargetType.set(plannedSampleId, activeTargetType);
  }

  const existingTargets = plannedSampleToTargets.get(plannedSampleId);
  const combinedTargets = existingTargets ? unionSets(existingTargets, currentTargets) : new Set(currentTargets);
  plannedSampleToTargets.set(plannedSampleId, combinedTargets);

  currentTargets.forEach((targetId) => {
    const samplesForTarget = targetToPlannedSamples.get(targetId);
    if (samplesForTarget) {
      // Create a new Set to maintain immutability
      targetToPlannedSamples.set(targetId, new Set([...samplesForTarget, plannedSampleId]));
    } else {
      targetToPlannedSamples.set(targetId, new Set([plannedSampleId]));
    }
  });
};

const handleSampleDeselection = (
  plannedSampleId: SampleDefinitionFormData['api_id'],
  currentTargets: Set<TargetApiId>,
  maps: {
    plannedSampleToTargetType: Map<SampleDefinitionFormData['api_id'], TargetType>;
    plannedSampleToTargets: Map<SampleDefinitionFormData['api_id'], Set<TargetApiId>>;
    targetToPlannedSamples: Map<TargetApiId, Set<SampleDefinitionFormData['api_id']>>;
  }
) => {
  const { plannedSampleToTargetType, plannedSampleToTargets, targetToPlannedSamples } = maps;

  const existingTargets = plannedSampleToTargets.get(plannedSampleId);
  if (!existingTargets) {
    return;
  }

  const updatedTargets = new Set(existingTargets);
  let hasChanges = false;

  currentTargets.forEach((targetId) => {
    if (updatedTargets.delete(targetId)) {
      hasChanges = true;
    }
  });

  if (!hasChanges) {
    return;
  }

  currentTargets.forEach((targetId) => {
    const samplesForTarget = targetToPlannedSamples.get(targetId);
    if (samplesForTarget) {
      // Create a new Set to maintain immutability
      const newSamplesForTarget = new Set(samplesForTarget);
      newSamplesForTarget.delete(plannedSampleId);
      if (newSamplesForTarget.size === 0) {
        targetToPlannedSamples.delete(targetId);
      } else {
        targetToPlannedSamples.set(targetId, newSamplesForTarget);
      }
    }
  });

  if (updatedTargets.size > 0) {
    plannedSampleToTargets.set(plannedSampleId, updatedTargets);
  } else {
    plannedSampleToTargets.delete(plannedSampleId);
    plannedSampleToTargetType.delete(plannedSampleId);
  }
};

export const assignAnimalStateInitializer = (props: {
  assignAnimalFormData: ReadonlyMap<SampleDefinitionFormData['api_id'], SampleDefinitionTargetFormData>;
}): AssignAnimalState => {
  const { assignAnimalFormData } = props;

  const selectedGroupTargets = new Set<GroupTarget['api_id']>();
  const selectedRegisteredAnimalTargets = new Set<RegisteredAnimalTarget['api_id']>();
  const selectedPlannedAnimalTargets = new Set<PlannedAnimalTarget['api_id']>();

  const targetToPlannedSamples = new Map<TargetApiId, Set<SampleDefinitionFormData['api_id']>>();
  const plannedSampleToTargets = new Map<SampleDefinitionFormData['api_id'], Set<TargetApiId>>();
  const plannedSampleToTargetType = new Map<SampleDefinitionFormData['api_id'], TargetType>();

  assignAnimalFormData.forEach(
    (targetConfig: SampleDefinitionTargetFormData, plannedSampleId: SampleDefinitionFormData['api_id']) => {
      if (targetConfig.type === 'no_target') {
        return;
      }

      const targets = 'targets' in targetConfig ? targetConfig.targets : [];

      if (targets.length === 0) {
        return;
      }

      plannedSampleToTargetType.set(plannedSampleId, targetConfig.type);

      const targetSet = new Set<TargetApiId>(targets);
      plannedSampleToTargets.set(plannedSampleId, targetSet);

      targets.forEach((targetId: TargetApiId) => {
        switch (targetConfig.type) {
          case 'groups':
            selectedGroupTargets.add(targetId as GroupApiId);
            break;
          case 'planned_animals':
            selectedPlannedAnimalTargets.add(targetId as AnimalDefinitionApiId);
            break;
          case 'registered_animals':
            selectedRegisteredAnimalTargets.add(targetId as AnimalApiId);
            break;
        }

        const samplesForTarget = targetToPlannedSamples.get(targetId);
        if (samplesForTarget) {
          samplesForTarget.add(plannedSampleId);
        } else {
          targetToPlannedSamples.set(targetId, new Set([plannedSampleId]));
        }
      });
    }
  );

  let activeTargetType: Exclude<TargetType, 'no_target'> = 'groups';
  if (selectedGroupTargets.size > 0) {
    activeTargetType = 'groups';
  } else if (selectedPlannedAnimalTargets.size > 0) {
    activeTargetType = 'planned_animals';
  } else if (selectedRegisteredAnimalTargets.size > 0) {
    activeTargetType = 'registered_animals';
  }

  return {
    activeTargetType,
    selectedGroupTargets,
    selectedRegisteredAnimalTargets,
    selectedPlannedAnimalTargets,
    targetToPlannedSamples,
    plannedSampleToTargets,
    plannedSampleToTargetType,
  };
};

export const assignAnimalReducer: Reducer<AssignAnimalState, AssignAnimalAction> = (prevState, action) => {
  switch (action.type) {
    case 'update-active-target-type': {
      return { ...prevState, activeTargetType: action.value };
    }
    case 'update-group-target-selection': {
      return { ...prevState, selectedGroupTargets: action.value };
    }
    case 'update-planned-animal-target-selection': {
      return { ...prevState, selectedPlannedAnimalTargets: action.value };
    }
    case 'update-registered-animal-target-selection': {
      return { ...prevState, selectedRegisteredAnimalTargets: action.value };
    }
    case 'update-planned-sample-selection': {
      if (action.value.size === 0) {
        return prevState;
      }

      const currentlySelectedTargets = getCurrentlySelectedTargets(prevState);

      if (currentlySelectedTargets.size === 0) {
        const hasSelectionsToAdd = Array.from(action.value.values()).some((isSelected) => isSelected);
        if (!hasSelectionsToAdd) {
          const hasExistingAssignments = Array.from(action.value.keys()).some((sampleId) =>
            prevState.plannedSampleToTargets.has(sampleId)
          );
          if (!hasExistingAssignments) {
            return prevState;
          }
        }
      }

      const newPlannedSampleToTargetType = new Map(prevState.plannedSampleToTargetType);
      const newPlannedSampleToTargets = new Map(prevState.plannedSampleToTargets);
      const newTargetToPlannedSamples = new Map(prevState.targetToPlannedSamples);

      const maps = {
        plannedSampleToTargetType: newPlannedSampleToTargetType,
        plannedSampleToTargets: newPlannedSampleToTargets,
        targetToPlannedSamples: newTargetToPlannedSamples,
      };

      action.value.forEach((isSelected, plannedSampleId) => {
        if (isSelected) {
          handleSampleSelection(plannedSampleId, currentlySelectedTargets, prevState.activeTargetType, maps);
        } else {
          handleSampleDeselection(plannedSampleId, currentlySelectedTargets, maps);
        }
      });

      return {
        ...prevState,
        plannedSampleToTargetType: newPlannedSampleToTargetType,
        plannedSampleToTargets: newPlannedSampleToTargets,
        targetToPlannedSamples: newTargetToPlannedSamples,
      };
    }
  }
  return prevState;
};

/**
 * Aggregates planned samples from multiple target IDs with automatic deduplication.
 *
 * This utility efficiently collects samples assigned to multiple targets (groups, planned animals,
 * registered animals) and ensures each sample appears only once in the result.
 *
 * @param targetIds - Array of target API IDs to aggregate samples from
 * @param targetToPlannedSamples - Map of target IDs to their assigned sample IDs
 * @param plannedSampleOptions - Map of sample IDs to sample option objects
 * @returns Array of unique planned sample options
 */
export const aggregateSamplesForAnimal = (
  targetIds: Array<Nullable<TargetApiId>>,
  targetToPlannedSamples: Map<TargetApiId, Set<SampleDefinitionFormData['api_id']>>,
  plannedSampleOptions: Map<PlannedSampleOption['api_id'], PlannedSampleOption>
): Array<PlannedSampleOption> => {
  const uniqueSampleIds = targetIds.reduce((acc, targetId) => {
    if (_notNil(targetId)) {
      targetToPlannedSamples.get(targetId)?.forEach((id) => acc.add(id));
    }
    return acc;
  }, new Set<SampleDefinitionFormData['api_id']>());
  return Array.from(uniqueSampleIds)
    .reduce<Array<PlannedSampleOption>>((acc, id) => {
      const sample = plannedSampleOptions.get(id);
      if (_notNil(sample)) {
        acc.push(sample);
      }
      return acc;
    }, [])
    .sort((left, right) =>
      (left.sample_name ?? '').localeCompare(right.sample_name ?? '', undefined, { numeric: true })
    );
};
